import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';
import LoginPopup from './components/LoginPopup';
import SignupPopup from './components/SignupPopup';
import UserAvatar from './components/common/UserAvatar';
import { useAuth } from './context/AuthContext';

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [popupType, setPopupType] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showOnlineClassesDropdown, setShowOnlineClassesDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { user, logout, isAuthExpired } = useAuth();

  const userIconRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileMenuBtnRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({});
  const authExpiredListenerRef = useRef(null);

  // Close mobile menu function
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  // Close dropdown function
  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
  }, []);

  // Calculate optimal dropdown position with viewport awareness
  const calculateDropdownPosition = useCallback((iconElement) => {
    if (!iconElement) return {};

    const rect = iconElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = 200; // Estimated dropdown height
    const dropdownWidth = 220; // Minimum dropdown width

    let top = rect.bottom + 8;
    let right = viewportWidth - rect.right;

    // Adjust if dropdown would go below viewport
    if (rect.bottom + dropdownHeight > viewportHeight) {
      top = rect.top - dropdownHeight - 8;
    }

    // Adjust if dropdown would go outside left edge
    if (right + dropdownWidth > viewportWidth) {
      right = viewportWidth - rect.left - dropdownWidth;
    }

    // Ensure minimum distance from edges
    right = Math.max(10, right);
    top = Math.max(10, top);

    // Convert to fixed positioning
    return {
      top: top,
      right: Math.max(10, right)
    };
  }, []);

  // Update dropdown position
  useEffect(() => {
    if (showDropdown && userIconRef.current && window.innerWidth > 768) {
      const position = calculateDropdownPosition(userIconRef.current);
      setDropdownPosition(position);
    }
  }, [showDropdown, calculateDropdownPosition]);

  // Update dropdown position on scroll and resize for desktop
  useEffect(() => {
    let scrollTimeout;

    const updateDropdownPosition = () => {
      if (showDropdown && userIconRef.current && window.innerWidth > 768) {
        const position = calculateDropdownPosition(userIconRef.current);
        setDropdownPosition(position);
      }
    };

    const debouncedUpdatePosition = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateDropdownPosition, 10);
    };

    const handleScroll = () => {
      if (showDropdown && window.innerWidth > 768) {
        // For scroll, update immediately but also debounce for performance
        updateDropdownPosition();
        debouncedUpdatePosition();
      }
    };

    if (showDropdown && window.innerWidth > 768) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', updateDropdownPosition);
    }

    return () => {
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [showDropdown, calculateDropdownPosition]);

  // Check if current page should have black background
  const isBlackBackground =
    location.pathname === '/about' ||
    location.pathname === '/blog' ||
    location.pathname.startsWith('/blog/') ||
    location.pathname === '/contactme' ||
    location.pathname === '/classes' ||
    location.pathname === '/events' ||
    location.pathname === '/online-private-classes' ||
    location.pathname === '/online-group-classes' ||
    location.pathname === '/store' ||
    location.pathname === '/profile' ||
    location.pathname === '/store-profile' ||
    location.pathname === '/orders' ||
    location.pathname.startsWith('/admin');

  // Handle scroll events with requestAnimationFrame for 60fps performance
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrolled = window.pageYOffset > 10;
          if (isScrolled !== scrolled) {
            setIsScrolled(scrolled);
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

  // Handle click outside events
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle dropdown clicks - only if not in mobile menu
      if (showDropdown && (!isMobileMenuOpen || window.innerWidth > 768)) {
        const isClickOnUserIcon = userIconRef.current?.contains(event.target);
        const isClickOnDropdown = event.target.closest('.user-dropdown');

        if (!isClickOnUserIcon && !isClickOnDropdown) {
          closeDropdown();
        }
      }

      // Handle mobile menu clicks
      if (isMobileMenuOpen) {
        const isClickOnMobileMenu = mobileMenuRef.current?.contains(event.target);
        const isClickOnMobileBtn = mobileMenuBtnRef.current?.contains(event.target);

        if (!isClickOnMobileMenu && !isClickOnMobileBtn) {
          closeMobileMenu();
          // Also close dropdown when closing mobile menu
          if (showDropdown) {
            closeDropdown();
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, isMobileMenuOpen, closeDropdown, closeMobileMenu]);

  // Handle route changes - close menus when navigating
  useEffect(() => {
    closeMobileMenu();
    closeDropdown();
  }, [location.pathname, closeMobileMenu, closeDropdown]);

  // Handle auth expiration events
  useEffect(() => {
    const handleAuthExpired = async () => {
      console.warn('Auth expiration detected in Navbar');
      setIsLoggingOut(true);
      closeDropdown();
      closeMobileMenu();

      // Wait a moment for user to see the notification
      setTimeout(() => {
        setIsLoggingOut(false);
      }, 1500);
    };

    authExpiredListenerRef.current = handleAuthExpired;
    window.addEventListener('authExpired', handleAuthExpired);

    return () => {
      window.removeEventListener('authExpired', handleAuthExpired);
    };
  }, [closeDropdown, closeMobileMenu]);

  // Auto-close dropdown if auth expires
  useEffect(() => {
    if (isAuthExpired) {
      closeDropdown();
      closeMobileMenu();
    }
  }, [isAuthExpired, closeDropdown, closeMobileMenu]);

  // Handle body scroll lock for mobile menu
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Handle keyboard events (ESC to close menus)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (isMobileMenuOpen) {
          closeMobileMenu();
        }
        if (showDropdown) {
          closeDropdown();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen, showDropdown, closeMobileMenu, closeDropdown]);

  // Handle user icon click
  const handleUserIconClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // Determine if we're in mobile mode
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      // On mobile, ensure menu is open first, then toggle dropdown
      if (!isMobileMenuOpen) {
        setIsMobileMenuOpen(true);
        // Wait for menu animation, then show dropdown
        setTimeout(() => {
          setShowDropdown(true);
        }, 100);
      } else {
        // Menu is already open, just toggle dropdown
        setShowDropdown(!showDropdown);
      }
    } else {
      // Desktop behavior
      if (isMobileMenuOpen) {
        closeMobileMenu();
      }
      setShowDropdown(!showDropdown);

      // Update position immediately for desktop
      if (userIconRef.current) {
        const position = calculateDropdownPosition(userIconRef.current);
        setDropdownPosition(position);
      }
    }
  };

  // Toggle mobile menu
  const toggleMobileMenu = (e) => {
    e.stopPropagation();
    setIsMobileMenuOpen(!isMobileMenuOpen);

    // Close dropdown if open
    if (showDropdown) {
      closeDropdown();
    }
  };

  // Check if link is active
  const isActiveLink = (path) => {
    if (path === '/classes') {
      return location.pathname.startsWith('/classes') || location.pathname.startsWith('/events');
    }
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Handle navigation link clicks
  const handleNavLinkClick = (path) => {
    // Close mobile menu immediately
    closeMobileMenu();

    // Navigate programmatically for consistent behavior
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  // Handle dropdown navigation
  const handleDropdownNavigation = (path) => {
    navigate(path);
    closeDropdown();
    closeMobileMenu(); // Also close mobile menu if open
  };

  // Handle logout with loading state
  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Dropdown component
  const DropdownMenu = ({ isMobile = false }) => {
    const dropdownClass = isMobile ? "user-dropdown mobile-dropdown" : "user-dropdown dropdown-portal";
    const dropdownStyle = isMobile ? {} : dropdownPosition;

    return (
      <div className={dropdownClass} style={dropdownStyle} role="menu" aria-label="User menu">
        <div className="dropdown-header">
          <span>Hi, {user?.first_name || user?.name || 'User'}!</span>
          <small>{user?.role || 'User'}</small>
        </div>
        <button
          className="dropdown-item"
          onClick={() => handleDropdownNavigation('/orders')}
          role="menuitem"
          onTouchStart={(e) => e.stopPropagation()}
        >
          My Orders
        </button>
        <button
          className="dropdown-item"
          onClick={() => handleDropdownNavigation('/store-profile')}
          role="menuitem"
          onTouchStart={(e) => e.stopPropagation()}
        >
          My Profile
        </button>
        {user?.role === 'admin' && (
          <button
            className="dropdown-item admin-item"
            onClick={() => handleDropdownNavigation('/admin/dashboard')}
            role="menuitem"
            onTouchStart={(e) => e.stopPropagation()}
          >
            Admin Dashboard
          </button>
        )}
        <button
          className="dropdown-item logout-item"
          onClick={handleLogout}
          disabled={isLoggingOut}
          role="menuitem"
          onTouchStart={(e) => e.stopPropagation()}
        >
          {isLoggingOut ? 'Logging out...' : 'Logout'}
        </button>
      </div>
    );
  };

  return (
    <>
      <nav className={`navbar ${isBlackBackground ? 'navbar-black' : ''} ${isScrolled ? 'navbar-scrolled' : ''}`}>
        <div className="logo">
          <Link to="/" onClick={() => handleNavLinkClick('/')}>
            <img src="/logo.png" alt="Dance Studio Logo" />
            <span className="logo-name">MWA</span>
          </Link>
        </div>

        <button
          ref={mobileMenuBtnRef}
          className="mobile-menu-btn"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
          type="button"
        >
          {isMobileMenuOpen ? '✕' : '☰'}
        </button>

        <ul
          ref={mobileMenuRef}
          className={`nav-links ${isMobileMenuOpen ? 'active' : ''}`}
        >
          <li>
            <button
              className={`nav-link-btn ${isActiveLink('/') ? 'active' : ''}`}
              onClick={() => handleNavLinkClick('/')}
              type="button"
            >
              Home
            </button>
          </li>
          <li>
            <button
              className={`nav-link-btn ${isActiveLink('/about') ? 'active' : ''}`}
              onClick={() => handleNavLinkClick('/about')}
              type="button"
            >
              About Us
            </button>
          </li>
          <li>
            <button
              className={`nav-link-btn ${isActiveLink('/classes') ? 'active' : ''}`}
              onClick={() => handleNavLinkClick('/classes')}
              type="button"
            >
              Events & Classes
            </button>
          </li>
          <li
            className="nav-item-dropdown"
            onMouseEnter={() => window.innerWidth > 768 && setShowOnlineClassesDropdown(true)}
            onMouseLeave={() => window.innerWidth > 768 && setShowOnlineClassesDropdown(false)}
          >
            <button
              className={`nav-link-btn ${isActiveLink('/online-private-classes') || isActiveLink('/online-group-classes') ? 'active' : ''}`}
              onClick={() => {
                if (window.innerWidth <= 768) {
                  setShowOnlineClassesDropdown(!showOnlineClassesDropdown);
                } else {
                  handleNavLinkClick('/online-private-classes');
                }
              }}
              type="button"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              Online Classes
              <span style={{ fontSize: '0.7em', marginLeft: '6px', transform: showOnlineClassesDropdown ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
            </button>

            {showOnlineClassesDropdown && (
              <div className="nav-dropdown-menu">
                <button onClick={() => handleNavLinkClick('/online-private-classes')}>Online Private Classes</button>
                <button onClick={() => handleNavLinkClick('/online-group-classes')}>Online Group Classes</button>
              </div>
            )}
          </li>
          <li>
            <button
              className={`nav-link-btn ${isActiveLink('/blog') ? 'active' : ''}`}
              onClick={() => handleNavLinkClick('/blog')}
              type="button"
            >
              Blog
            </button>
          </li>
          <li>
            <button
              className={`nav-link-btn ${isActiveLink('/store') ? 'active' : ''}`}
              onClick={() => handleNavLinkClick('/store')}
              type="button"
            >
              Store
            </button>
          </li>
          <li>
            <button
              className={`nav-link-btn ${isActiveLink('/contactme') ? 'active' : ''}`}
              onClick={() => handleNavLinkClick('/contactme')}
              type="button"
            >
              Contact me
            </button>
          </li>
          <li>
            <button
              className={`nav-link-btn ${isActiveLink('/faq') ? 'active' : ''}`}
              onClick={() => handleNavLinkClick('/faq')}
              type="button"
            >
              FAQs
            </button>
          </li>
          <li>
            {user ? (
              <div className={`user-icon-container ${showDropdown ? 'dropdown-open' : ''}`}>
                <button
                  ref={userIconRef}
                  className="user-icon"
                  onClick={handleUserIconClick}
                  aria-label="User menu"
                  aria-expanded={showDropdown}
                  aria-haspopup="true"
                  type="button"
                >
                  <UserAvatar
                    firstName={user.first_name}
                    lastName={user.last_name}
                    size={40}
                  />
                </button>
                {showDropdown && (
                  // Conditional rendering based on mobile state
                  isMobileMenuOpen && window.innerWidth <= 768
                    ? <DropdownMenu isMobile={true} />
                    : window.innerWidth > 768 && ReactDOM.createPortal(<DropdownMenu isMobile={false} />, document.body)
                )}
              </div>
            ) : (
              <button
                className="nav-link-btn"
                onClick={() => {
                  setPopupType('login');
                  closeMobileMenu();
                }}
                type="button"
              >
                Login
              </button>
            )}
          </li>
        </ul>
      </nav>

      {/* Mobile backdrop */}
      <div
        className={`mobile-backdrop ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      {/* Login/Signup Popups */}
      {popupType === 'login' && (
        <LoginPopup
          onClose={() => setPopupType(null)}
          onSignupClick={() => setPopupType('signup')}
        />
      )}
      {popupType === 'signup' && (
        <SignupPopup
          onClose={() => setPopupType(null)}
          onLoginClick={() => setPopupType('login')}
        />
      )}
    </>
  );
}

export default Navbar;